/**
 * Analytics helper – tracks events via Firebase Analytics (GA4).
 * Safe to call when analytics is not initialized (no-op).
 */
import { logEvent } from 'firebase/analytics';
import { analytics } from './firebase-config.js';
import {
  trackInitiateCheckout as metaInitiateCheckout,
  trackMetaPageView
} from './meta-pixel.js';

/**
 * Track a custom event. No-op if Firebase Analytics is not initialized.
 * @param {string} eventName - GA4 event name (e.g. 'file_download', 'sign_up', 'begin_checkout')
 * @param {Record<string, string | number | boolean>} [params] - Optional event parameters
 */
export function trackEvent(eventName, params = {}) {
  if (!analytics) return;
  try {
    logEvent(analytics, eventName, params);
  } catch (err) {
    console.warn('Analytics trackEvent failed:', err);
  }
}

/**
 * Track a SPA "page visit" in GA4.
 * GA4 only records page views automatically on full page loads; for in-app views
 * (like opening the Journal in the dashboard) we explicitly send `page_view`.
 * @param {string} pagePath - Path + optional query (e.g. '/journal.html?plan=abc')
 * @param {string} [pageTitle] - Optional page title override
 */
export function trackPageView(pagePath, pageTitle = '') {
  if (analytics) {
    try {
      const origin = (typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : '';
      const location = origin ? `${origin}${pagePath.startsWith('/') ? '' : '/'}${pagePath}` : pagePath;
      const title = pageTitle || (typeof document !== 'undefined' ? document.title : '');
      logEvent(analytics, 'page_view', {
        page_location: location,
        page_path: pagePath,
        page_title: title
      });
    } catch (err) {
      console.warn('Analytics trackPageView failed:', err);
    }
  }
  trackMetaPageView();
}

/** Track button/link click in the app */
export function trackClick(buttonName, context = {}) {
  trackEvent('click', { button_name: buttonName, ...context });
}

/** Track file download (PDF, image, calendar) */
export function trackDownload(fileType, fileName = '') {
  trackEvent('file_download', { file_type: fileType, file_name: fileName });
}

/** Track sign up completion */
export function trackSignUp(method = 'email') {
  trackEvent('sign_up', { method });
}

/** Track click/redirect to Lemon Squeezy checkout */
export function trackCheckoutClick(productId = '', source = '') {
  trackEvent('begin_checkout', { checkout_provider: 'lemonsqueezy', product_id: productId, source });
  metaInitiateCheckout();
}
